/* eslint-disable jsx-a11y/media-has-caption */
import { useEffect, useRef, useState, useCallback } from 'react';
// material
import {
    Box,
    Dialog,
    Typography,
    Avatar,
    IconButton,
    Stack,
    Grid
} from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
// redux
import { useSelector, useDispatch } from 'react-redux';
import { resetCall, updateCallStatus } from '../../redux/slices/internalChat';
// socket
import { socket } from '../../contexts/SocketIo';
import { callEvents } from '../../redux/middleware/chatEventsMiddleware';
// utils
import { soundManager } from '../../utils/soundManager';
import useAuth from '../../hooks/useAuth';
// components
import Iconify from '../../components/Iconify';
import { HOST_API } from '../../config';

// ----------------------------------------------------------------------

const pcConfig = {
    iceServers: [{ urls: 'stun:stun.l.google.com:19302' }]
};

const RootStyle = styled(Box)(({ theme }) => ({
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.palette.grey[900],
    color: theme.palette.common.white,
    position: 'relative',
    overflow: 'hidden',
    borderRadius: theme.shape.borderRadius
}));

const VideoContainer = styled(Box)(() => ({
    width: '100%',
    height: '100%',
    position: 'relative',
    backgroundColor: '#000',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
}));

const RemoteVideo = styled('video', {
    shouldForwardProp: (prop) => prop !== 'isSharingScreen'
})(({ isSharingScreen }) => ({
    width: '100%',
    height: '100%',
    objectFit: isSharingScreen ? 'contain' : 'cover'
}));

const LocalVideo = styled('video')(({ theme }) => ({
    width: 150,
    height: 200,
    objectFit: 'cover',
    position: 'absolute',
    top: 20,
    right: 20,
    borderRadius: theme.shape.borderRadius,
    border: `2px solid ${theme.palette.common.white}`,
    backgroundColor: theme.palette.grey[800],
    zIndex: 10
}));

const ControlsStyle = styled(Stack)(({ theme }) => ({
    position: 'absolute',
    bottom: 40,
    left: '50%',
    transform: 'translateX(-50%)',
    flexDirection: 'row',
    gap: theme.spacing(3),
    padding: theme.spacing(2),
    borderRadius: 40,
    backgroundColor: alpha(theme.palette.common.black, 0.4),
    backdropFilter: 'blur(10px)',
    zIndex: 20
}));

const ActionButtonStyle = styled(IconButton)(({ theme, color }) => ({
    width: 56,
    height: 56,
    backgroundColor: color === 'error' ? theme.palette.error.main : alpha(theme.palette.common.white, 0.2),
    color: theme.palette.common.white,
    '&:hover': {
        backgroundColor: color === 'error' ? theme.palette.error.dark : alpha(theme.palette.common.white, 0.3),
    }
}));

const DurationStyle = styled(Typography)(({ theme }) => ({
    position: 'absolute',
    top: 20,
    left: '50%',
    transform: 'translateX(-50%)',
    padding: theme.spacing(0.5, 2),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: alpha(theme.palette.common.black, 0.5),
    color: theme.palette.common.white,
    zIndex: 30
}));

// ----------------------------------------------------------------------

const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

// ----------------------------------------------------------------------

export default function InternalCallModal() {
    const dispatch = useDispatch();
    const { user } = useAuth();
    const { call } = useSelector((state) => state.internalChat);
    const { status, partner, type, isIncoming, chatId, participantIds } = call;
    const chat = useSelector((state) => state.internalChat.conversations.byId[chatId]);

    const [localStream, setLocalStream] = useState(null);
    const [remoteStreams, setRemoteStreams] = useState({});

    // Auto-disable video immediately if group is > 4
    const isLargeGroup = participantIds && participantIds.length > 4;
    const [isMuted, setIsMuted] = useState(false);
    const [isVideoOff, setIsVideoOff] = useState(type === 'audio' || (type === 'video' && isLargeGroup));
    const [duration, setDuration] = useState(0);

    const peersRef = useRef({});
    const localVideoRef = useRef(null);
    const localScreenStreamRef = useRef(null);
    const [isScreenSharing, setIsScreenSharing] = useState(false);
    const [remoteScreenSharing, setRemoteScreenSharing] = useState({});

    const isMountedRef = useRef(true);
    const timerRef = useRef(null);
    const startTimeRef = useRef(null);
    const pendingOffersRef = useRef({});
    const pendingCandidatesRef = useRef({});

    const open = status !== 'idle';

    const cleanup = useCallback(() => {
        Object.values(peersRef.current).forEach(pc => pc.close());
        peersRef.current = {};

        if (localStream) {
            localStream.getTracks().forEach(track => track.stop());
            setLocalStream(null);
        }
        if (localScreenStreamRef.current) {
            localScreenStreamRef.current.getTracks().forEach(track => track.stop());
            localScreenStreamRef.current = null;
        }
        setRemoteStreams({});
        setIsScreenSharing(false);
        setRemoteScreenSharing({});
        pendingOffersRef.current = {};
        pendingCandidatesRef.current = {};
    }, [localStream]);

    const handleHangup = useCallback(() => {
        let finalDuration = 0;
        if (startTimeRef.current) {
            finalDuration = Math.floor((Date.now() - startTimeRef.current) / 1000);
        }

        socket.emit('internal_chat:call_hangup', {
            targetId: partner?._id,
            chatId,
            participantIds,
            status: status === 'active' ? 'completed' : 'missed',
            duration: finalDuration,
            callType: type
        });

        cleanup();
        dispatch(resetCall());
    }, [chatId, cleanup, dispatch, partner?._id, participantIds, status, type]);

    const removePeer = useCallback((peerId) => {
        let hadPeer = false;
        if (peersRef.current[peerId]) {
            peersRef.current[peerId].close();
            delete peersRef.current[peerId];
            hadPeer = true;
        }
        setRemoteStreams(prev => {
            const next = { ...prev };
            delete next[peerId];
            return next;
        });

        // If 1v1 call and peer disconnected, or last person left
        if (!participantIds || participantIds.length <= 2) {
            handleHangup();
        } else if (status === 'active' && Object.keys(peersRef.current).length === 0 && hadPeer) {
            handleHangup();
        }
    }, [handleHangup, participantIds, status]);

    const handleReject = useCallback(() => {
        socket.emit('internal_chat:call_reject', {
            targetId: partner?._id,
            chatId,
            callType: type,
            participantIds
        });
        cleanup();
        dispatch(resetCall());
    }, [chatId, cleanup, dispatch, partner?._id, participantIds, type]);

    const handleTimeout = useCallback(() => {
        socket.emit('internal_chat:call_timeout', {
            targetId: partner?._id,
            chatId,
            participantIds,
            callType: type
        });
        cleanup();
        dispatch(resetCall());
    }, [chatId, cleanup, dispatch, partner?._id, participantIds, type]);

    const setupPeerConnection = useCallback((stream, targetId) => {
        const pc = new RTCPeerConnection(pcConfig);
        peersRef.current[targetId] = pc;

        // Add tracks
        stream.getTracks().forEach(track => {
            // Respect initial video off for large groups
            if (track.kind === 'video' && isVideoOff) track.enabled = false;
            if (track.kind === 'audio' && isMuted) track.enabled = false;
            pc.addTrack(track, stream);
        });

        // ICE Candidates
        pc.onicecandidate = (event) => {
            if (event.candidate) {
                socket.emit('internal_chat:call_ice_candidate', {
                    targetId,
                    candidate: event.candidate,
                    chatId
                });
            }
        };

        // Remote Stream
        pc.ontrack = (event) => {
            if (event.streams && event.streams[0]) {
                setRemoteStreams(prev => ({
                    ...prev,
                    [targetId]: event.streams[0]
                }));
            }
        };

        return pc;
    }, [chatId, isVideoOff, isMuted]);

    const processOffer = useCallback(async (offer, fromId, existingPc) => {
        const pc = existingPc || peersRef.current[fromId];
        if (!pc) return;

        try {
            await pc.setRemoteDescription(new RTCSessionDescription(offer));

            if (pendingCandidatesRef.current[fromId]) {
                await Promise.all(
                    pendingCandidatesRef.current[fromId].map((candidate) =>
                        pc.addIceCandidate(new RTCIceCandidate(candidate))
                    )
                );
                pendingCandidatesRef.current[fromId] = [];
            }

            const answer = await pc.createAnswer({
                offerToReceiveAudio: true,
                offerToReceiveVideo: type === 'video'
            });
            await pc.setLocalDescription(answer);
            socket.emit('internal_chat:call_answer', {
                targetId: fromId,
                answer,
                chatId
            });
        } catch (err) {
            console.error('Error handling offer:', err);
        }
    }, [chatId, type]);

    const handleAccept = async () => {
        try {
            soundManager.stopRinging();
            socket.emit('internal_chat:call_accepted', { chatId });

            let videoConstraints = false;
            if (type === 'video') {
                videoConstraints = isLargeGroup ? {
                    width: { ideal: 640 }, height: { ideal: 480 }, frameRate: { ideal: 15 }
                } : {
                    width: { ideal: 1280, max: 1920 }, height: { ideal: 720, max: 1080 }, frameRate: { ideal: 24, max: 30 }
                };
            }

            let stream;
            try {
                stream = await navigator.mediaDevices.getUserMedia({
                    audio: true,
                    video: videoConstraints
                });
            } catch (mediaError) {
                console.warn('[WebRTC] Falha ao capturar vídeo (sem webcam ou permissão). Tentando apenas áudio.', mediaError);
                if (videoConstraints) {
                    try {
                        stream = await navigator.mediaDevices.getUserMedia({
                            audio: true,
                            video: false
                        });
                        // Override video off state naturally
                        if (!isVideoOff) toggleVideo();
                    } catch (audioFallbackError) {
                        console.error('[WebRTC] Falha crítica: Dispositivo sem microfone também.', audioFallbackError);
                        throw audioFallbackError; // Vai pro catch geral do handleHangup()
                    }
                } else {
                    throw mediaError;
                }
            }

            setLocalStream(stream);
            dispatch(updateCallStatus('active'));

            const myId = user._id;
            const targets = participantIds ? participantIds.filter(id => id !== myId) : [partner._id];

            targets.forEach(async (targetId) => {
                const pc = setupPeerConnection(stream, targetId);

                // If I am active and receiver, I already have pending offers from whoever called.
                if (pendingOffersRef.current[targetId]) {
                    await processOffer(pendingOffersRef.current[targetId], targetId, pc);
                    delete pendingOffersRef.current[targetId];
                } else if (myId > targetId) {
                    // Send Offer to other receivers in the group to setup mesh
                    const offer = await pc.createOffer({ offerToReceiveAudio: true, offerToReceiveVideo: type === 'video' });
                    await pc.setLocalDescription(offer);
                    socket.emit('internal_chat:call_offer', { targetId, offer, chatId });
                }
            });
        } catch (error) {
            console.error('Failed to get user media:', error);
            handleHangup();
        }
    };

    // --- SOUND EFFECTS ---
    useEffect(() => {
        if (status === 'ringing') {
            soundManager.startRinging(isIncoming ? 'incoming' : 'outgoing');
        } else if (status === 'calling') {
            soundManager.startRinging('outgoing');
        } else {
            soundManager.stopRinging();
        }

        return () => {
            soundManager.stopRinging();
        };
    }, [status, isIncoming]);

    // --- TIMER & TIMEOUT ---
    useEffect(() => {
        if (status === 'calling' || status === 'ringing') {
            timerRef.current = setTimeout(() => {
                handleTimeout();
            }, 60000);
        } else if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }

        if (status === 'active') {
            startTimeRef.current = Date.now();
            const interval = setInterval(() => {
                setDuration(prev => prev + 1);
            }, 1000);
            return () => {
                clearInterval(interval);
                startTimeRef.current = null;
                setDuration(0);
            };
        }

        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, [status, handleTimeout]);

    // --- WINDOW CLOSE HANDLER ---
    useEffect(() => {
        const handleBeforeUnload = () => {
            if (status === 'active' || status === 'calling' || status === 'ringing') {
                handleHangup();
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [handleHangup, status]);

    // --- SIGNALING EFFECTS ---

    useEffect(() => {
        const onOffer = async (data) => {
            if (data.chatId !== chatId) return;
            const { fromId, offer } = data;

            let pc = peersRef.current[fromId];
            if (!pc && localStream && status === 'active') {
                pc = setupPeerConnection(localStream, fromId);
            }

            if (!pc) {
                pendingOffersRef.current[fromId] = offer;
                return;
            }
            await processOffer(offer, fromId, pc);
        };

        const onAnswer = async (data) => {
            if (data.chatId !== chatId) return;
            const { fromId, answer } = data;
            const pc = peersRef.current[fromId];
            if (!pc) return;

            try {
                await pc.setRemoteDescription(new RTCSessionDescription(answer));

                if (pendingCandidatesRef.current[fromId]) {
                    await Promise.all(
                        pendingCandidatesRef.current[fromId].map((candidate) =>
                            pc.addIceCandidate(new RTCIceCandidate(candidate))
                        )
                    );
                    pendingCandidatesRef.current[fromId] = [];
                }

                dispatch(updateCallStatus('active'));
            } catch (err) {
                console.error('Error handling answer:', err);
            }
        };

        const onIceCandidate = async (data) => {
            if (data.chatId !== chatId) return;
            const { fromId, candidate } = data;
            const pc = peersRef.current[fromId];

            if (pc && pc.remoteDescription) {
                try {
                    await pc.addIceCandidate(new RTCIceCandidate(candidate));
                } catch (err) {
                    console.error('Error adding ICE candidate:', err);
                }
            } else {
                if (!pendingCandidatesRef.current[fromId]) pendingCandidatesRef.current[fromId] = [];
                pendingCandidatesRef.current[fromId].push(candidate);
            }
        };

        const onHangup = (data) => {
            if (data.chatId === chatId) {
                removePeer(data.fromId);
            }
        };

        const onUserJoined = async (data) => {
            if (data.chatId !== chatId) return;
            const newUserId = data.fromId;

            if (peersRef.current[newUserId]) return;

            try {
                const pc = setupPeerConnection(localStream, newUserId);
                const offer = await pc.createOffer({ offerToReceiveAudio: true, offerToReceiveVideo: true });
                await pc.setLocalDescription(offer);

                socket.emit('internal_chat:call_offer', {
                    targetId: newUserId,
                    offer,
                    chatId
                });
            } catch (err) {
                console.error('[WebRTC] Falha ao conectar com o novo participante:', err);
            }
        };

        const onRequestOffer = async (data) => {
            if (data.chatId !== chatId) return;
            const peerId = data.fromId;
            let pc = peersRef.current[peerId];
            if (!pc && localStream && status === 'active') {
                pc = setupPeerConnection(localStream, peerId);
            }
            if (pc) {
                try {
                    const offer = await pc.createOffer({ offerToReceiveAudio: true, offerToReceiveVideo: true });
                    await pc.setLocalDescription(offer);
                    socket.emit('internal_chat:call_offer', { targetId: peerId, offer, chatId });
                } catch (err) { console.error('Failed to recreate offer', err); }
            }
        };

        const onScreenShareStatus = (data) => {
            if (data.chatId !== chatId) return;
            setRemoteScreenSharing(prev => ({
                ...prev,
                [data.fromId]: data.isSharing
            }));
        };

        callEvents.on('offer', onOffer);
        callEvents.on('answer', onAnswer);
        callEvents.on('ice-candidate', onIceCandidate);
        callEvents.on('hangup', onHangup);
        callEvents.on('rejected', onHangup);
        callEvents.on('user-joined', onUserJoined);
        callEvents.on('screen-share-status', onScreenShareStatus);
        callEvents.on('request-offer', onRequestOffer);

        return () => {
            callEvents.off('offer', onOffer);
            callEvents.off('answer', onAnswer);
            callEvents.off('ice-candidate', onIceCandidate);
            callEvents.off('hangup', onHangup);
            callEvents.off('rejected', onHangup);
            callEvents.off('user-joined', onUserJoined);
            callEvents.off('screen-share-status', onScreenShareStatus);
            callEvents.off('request-offer', onRequestOffer);
        };
    }, [chatId, dispatch, processOffer, removePeer, localStream, type, setupPeerConnection, status]);

    // --- INITIATOR EFFECT (E JOIN EARLY CAMERA CAPTURE) ---
    useEffect(() => {
        // Se formos o Iniciador Original ('calling' && !isIncoming) 
        // OU se entramos tardiamente via "Join Call" direto pro estado 'active' sem localStream ainda.
        const isOriginalInitiator = status === 'calling' && !isIncoming;
        const isLateJoiner = status === 'active' && !isIncoming && !localStream;

        if ((isOriginalInitiator || isLateJoiner) && Object.keys(peersRef.current).length === 0) {
            const initiate = async () => {
                try {
                    let videoConstraints = false;
                    if (type === 'video') {
                        videoConstraints = isLargeGroup ? {
                            width: { ideal: 640 }, height: { ideal: 480 }, frameRate: { ideal: 15 }
                        } : {
                            width: { ideal: 1280, max: 1920 }, height: { ideal: 720, max: 1080 }, frameRate: { ideal: 24, max: 30 }
                        };
                    }

                    let stream;
                    try {
                        stream = await navigator.mediaDevices.getUserMedia({
                            audio: true,
                            video: videoConstraints
                        });
                    } catch (mediaError) {
                        console.warn('[WebRTC] Falha ao capturar vídeo na iniciação. Tentando apenas áudio.', mediaError);
                        if (videoConstraints) {
                            try {
                                stream = await navigator.mediaDevices.getUserMedia({
                                    audio: true,
                                    video: false
                                });
                                // Force hide video
                                if (!isVideoOff) toggleVideo();
                            } catch (audioFallbackError) {
                                console.error('[WebRTC] Falha crítica de iniciação: sem microfone.', audioFallbackError);
                                throw audioFallbackError;
                            }
                        } else {
                            throw mediaError;
                        }
                    }

                    if (!isMountedRef.current) return;
                    setLocalStream(stream);

                    const myId = user?._id;
                    const targets = participantIds ? participantIds.filter(id => id !== myId) : [partner?._id];

                    // Se eu sou o Original, eu crio Offers pra todo mundo.
                    // Se eu sou o Late Joiner, eu NÃO crio Offers, pois eles já vão me convidar assim que escutarem meu socket de Joined. Mas eu preciso estar com a stream ativada.
                    if (isOriginalInitiator) {
                        targets.forEach(async (targetId) => {
                            const pc = setupPeerConnection(stream, targetId);
                            const offer = await pc.createOffer({
                                offerToReceiveAudio: true,
                                offerToReceiveVideo: true
                            });
                            await pc.setLocalDescription(offer);

                            socket.emit('internal_chat:call_offer', {
                                targetId,
                                offer,
                                chatId
                            });
                        });
                    } else if (isLateJoiner) {
                        socket.emit('internal_chat:call_join', { chatId, participantIds });
                        // Força todos os remotos a cuspir um pacote Offer para nós (Recuperação de Call Waiting e Glare WebRTC).
                        targets.forEach(targetId => {
                            socket.emit('internal_chat:call_request_offer', { targetId, chatId });
                        });
                    }
                } catch (error) {
                    console.error('Failed to initiate call:', error);
                    handleHangup();
                }
            };
            initiate();
        }
    }, [status, isIncoming, type, partner?._id, chatId, participantIds, setupPeerConnection, handleHangup, user?._id, isLargeGroup, isVideoOff, toggleVideo, localStream]);

    // --- VIDEO ELEMENT SYNC ---
    useEffect(() => {
        if (localVideoRef.current && localStream && localVideoRef.current.srcObject !== localStream) {
            localVideoRef.current.srcObject = localStream;
        }
    }, [localStream, status]);

    const setRemoteVideoRef = useCallback((element, stream) => {
        if (element && stream && element.srcObject !== stream) {
            element.srcObject = stream;
        }
    }, []);

    const toggleMute = useCallback(() => {
        if (localStream) {
            localStream.getAudioTracks().forEach(track => {
                track.enabled = isMuted;
            });
            setIsMuted(!isMuted);
        }
    }, [localStream, isMuted]);

    const toggleVideo = useCallback(() => {
        if (localStream) {
            localStream.getVideoTracks().forEach(track => {
                track.enabled = isVideoOff;
            });
            setIsVideoOff(!isVideoOff);
        }
    }, [localStream, isVideoOff]);

    const toggleScreenShare = useCallback(async () => {
        if (isScreenSharing) {
            // STOP SHARING AND REVERT TO CAMERA
            if (localScreenStreamRef.current) {
                localScreenStreamRef.current.getTracks().forEach(track => track.stop());
                localScreenStreamRef.current = null;
            }

            if (localStream) {
                const videoTrack = localStream.getVideoTracks()[0];
                if (videoTrack) {
                    Object.values(peersRef.current).forEach(pc => {
                        const sender = pc.getSenders().find(s => s.track && s.track.kind === 'video');
                        if (sender) sender.replaceTrack(videoTrack);
                    });
                }
            }
            setIsScreenSharing(false);

            // Broadcast off
            const targets = participantIds ? participantIds.filter(id => id !== user?._id) : [partner?._id];
            socket.emit('internal_chat:screen_share_status', {
                targetIds: targets.length > 1 ? targets : undefined,
                targetId: targets.length === 1 ? targets[0] : undefined,
                chatId,
                isSharing: false
            });
        } else {
            // START SHARING
            try {
                const displayStream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false });
                localScreenStreamRef.current = displayStream;
                const screenTrack = displayStream.getVideoTracks()[0];

                // Auto-revert if user clicks "Stop Sharing" on chrome floating native bar
                screenTrack.onended = () => {
                    toggleScreenShare();
                };

                // Replace Sender track
                Object.values(peersRef.current).forEach(pc => {
                    const sender = pc.getSenders().find(s => s.track && s.track.kind === 'video');
                    if (sender) sender.replaceTrack(screenTrack);
                });

                setIsScreenSharing(true);

                // Broadcast on
                const targets = participantIds ? participantIds.filter(id => id !== user?._id) : [partner?._id];
                socket.emit('internal_chat:screen_share_status', {
                    targetIds: targets.length > 1 ? targets : undefined,
                    targetId: targets.length === 1 ? targets[0] : undefined,
                    chatId,
                    isSharing: true
                });

            } catch (err) {
                console.error("Failed to share screen:", err);
            }
        }
    }, [isScreenSharing, localStream, participantIds, user?._id, partner?._id, chatId]);

    if (!open) return null;

    const remoteEntries = Object.entries(remoteStreams);

    const getPeerInfo = (peerId) => {
        if (!chat || !chat.participants) return { name: 'Usuário', avatar: '' };
        const participant = chat.participants.find(p => String(p.userId._id || p.userId) === String(peerId));
        if (participant && participant.userId) {
            return {
                name: participant.userId.name || 'Usuário',
                avatar: participant.userId.avatar || ''
            };
        }
        return { name: 'Usuário', avatar: '' };
    };

    return (
        <Dialog
            fullScreen
            open={open}
            onClose={(event, reason) => {
                if (reason !== 'backdropClick') handleHangup();
            }}
            PaperProps={{
                sx: { bgcolor: 'transparent', boxShadow: 'none' }
            }}
        >
            <RootStyle>
                {status === 'active' || (!isIncoming && status === 'calling') ? (
                    <VideoContainer>
                        {status === 'active' && (
                            <DurationStyle variant="subtitle2">
                                {formatDuration(duration)}
                            </DurationStyle>
                        )}

                        {/* Remote Group Videos Grid */}
                        {remoteEntries.length > 0 && status === 'active' ? (
                            <Grid container spacing={1} sx={{ height: '100%', width: '100%', p: 2 }}>
                                {remoteEntries.map(([peerId, stream]) => {
                                    const lgCount = remoteEntries.length;
                                    let mdCols = 12;
                                    if (lgCount > 3) mdCols = 4;
                                    else if (lgCount > 1) mdCols = 6;

                                    const peerInfo = getPeerInfo(peerId);

                                    return (
                                        <Grid item xs={12} sm={lgCount > 1 ? 6 : 12} md={mdCols} key={peerId} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: lgCount > 2 ? '50%' : '100%', position: 'relative' }}>
                                            {type === 'video' ? (
                                                <>
                                                    <RemoteVideo isSharingScreen={remoteScreenSharing[peerId]} ref={el => setRemoteVideoRef(el, stream)} autoPlay playsInline sx={{ borderRadius: 2 }} />
                                                    <Box sx={{ position: 'absolute', bottom: 16, left: 16, bgcolor: 'rgba(0,0,0,0.5)', px: 1.5, py: 0.5, borderRadius: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Avatar src={`${HOST_API}${peerInfo.avatar}`} sx={{ width: 24, height: 24 }} />
                                                        <Typography variant="caption" sx={{ color: 'white', fontWeight: 'bold' }}>{peerInfo.name}</Typography>
                                                    </Box>
                                                </>
                                            ) : (
                                                <>
                                                    <audio ref={el => setRemoteVideoRef(el, stream)} autoPlay playsInline style={{ display: 'none' }} />
                                                    <Box sx={{ textAlign: 'center' }}>
                                                        <Avatar src={`${HOST_API}${peerInfo.avatar}`} sx={{ width: 80, height: 80, mx: 'auto', mb: 2 }} />
                                                        <Typography variant="subtitle2">{peerInfo.name}</Typography>
                                                    </Box>
                                                </>
                                            )}
                                        </Grid>
                                    );
                                })}
                            </Grid>
                        ) : null}

                        {/* Fallback avatar if no remotes connected yet */}
                        {((remoteEntries.length === 0 && status === 'active') || status !== 'active') && (
                            <Box sx={{ textAlign: 'center', position: 'absolute' }}>
                                <Avatar
                                    src={`${HOST_API}${partner?.avatar}`}
                                    sx={{ width: 120, height: 120, mb: 2, mx: 'auto' }}
                                />
                                <Typography variant="h4">{partner?.name}</Typography>
                                <Typography variant="subtitle1" sx={{ opacity: 0.7 }}>
                                    {status === 'active' && 'Aguardando outros participantes ingressarem...'}
                                    {status === 'calling' && 'Chamando...'}
                                    {status !== 'active' && status !== 'calling' && 'Conectando...'}
                                </Typography>
                            </Box>
                        )}

                        {type === 'video' && localStream && (
                            <LocalVideo ref={localVideoRef} autoPlay playsInline muted />
                        )}
                    </VideoContainer>
                ) : (
                    /* Incoming Call UI */
                    <Box sx={{ textAlign: 'center' }}>
                        <Avatar
                            src={`${HOST_API}${partner?.avatar}`}
                            sx={{ width: 120, height: 120, mb: 2, mx: 'auto', border: '4px solid white', animation: 'pulse 2s infinite' }}
                        />
                        <Typography variant="h4">{partner?.name}</Typography>
                        <Typography variant="h6" sx={{ color: 'primary.main', mb: 4 }}>
                            Chamada de {type === 'video' ? 'Vídeo' : 'Voz'} recebida
                        </Typography>

                        <Stack direction="row" spacing={4} justifyContent="center">
                            <ActionButtonStyle color="error" onClick={handleReject}>
                                <Iconify icon="eva:close-fill" width={32} height={32} />
                            </ActionButtonStyle>
                            <ActionButtonStyle sx={{ bgcolor: 'success.main', '&:hover': { bgcolor: 'success.dark' } }} onClick={handleAccept}>
                                <Iconify icon="eva:phone-fill" width={32} height={32} />
                            </ActionButtonStyle>
                        </Stack>
                    </Box>
                )}

                {/* Active/Calling Controls */}
                {(status === 'active' || (!isIncoming && status === 'calling')) && (
                    <ControlsStyle>
                        <ActionButtonStyle onClick={toggleMute}>
                            <Iconify icon={isMuted ? "eva:mic-off-fill" : "eva:mic-fill"} width={24} height={24} />
                        </ActionButtonStyle>

                        <ActionButtonStyle onClick={toggleScreenShare} sx={{ bgcolor: isScreenSharing ? 'primary.main' : undefined, '&:hover': { bgcolor: isScreenSharing ? 'primary.dark' : undefined } }}>
                            <Iconify icon={isScreenSharing ? "eva:monitor-outline" : "eva:monitor-fill"} width={24} height={24} />
                        </ActionButtonStyle>

                        {type === 'video' && (
                            <ActionButtonStyle onClick={toggleVideo}>
                                <Iconify icon={isVideoOff ? "eva:video-off-fill" : "eva:video-fill"} width={24} height={24} />
                            </ActionButtonStyle>
                        )}

                        <ActionButtonStyle color="error" onClick={handleHangup}>
                            <Iconify icon="eva:phone-off-fill" width={24} height={24} />
                        </ActionButtonStyle>
                    </ControlsStyle>
                )}
            </RootStyle>

            <style>
                {`
                    @keyframes pulse {
                        0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.7); }
                        70% { transform: scale(1.05); box-shadow: 0 0 0 10px rgba(255, 255, 255, 0); }
                        100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(255, 255, 255, 0); }
                    }
                `}
            </style>
        </Dialog>
    );
}
